import React, { useState } from 'react';
import { Modal } from 'antd';
import s from './VideoDetailContent.module.scss';
import MindMapContent from './MindMapContent';
import MindMap from '@/components/MindMap';

/**
 * 视频详情内容组件
 * 用于展示视频的详细信息：关键词、摘要、思维导图
 */
const VideoDetailContent = ({ video, videoIndex, data, videoList = [], onViewNext, onContinueAsk, onVideoClick }) => {
    // 控制思维导图放大模态框的显示
    const [isMindMapExpanded, setIsMindMapExpanded] = useState(false);

    // 处理"查看下一个"按钮点击
    const handleViewNext = () => {
        // 传递包含 videoIndex 的完整 data 对象
        if (onViewNext) {
            onViewNext(data || { video, videoIndex });
        }
    };

    // 处理思维导图点击放大
    const handleMindMapClick = (e) => {
        // 如果点击的是放大按钮或容器本身，打开放大模态框
        if (e.target.closest(`.${s['mindmap-container']}`) || e.target.closest(`.${s['expand-icon']}`)) {
            setIsMindMapExpanded(true);
        }
    };

    // 关闭放大模态框
    const handleCloseExpanded = () => {
        setIsMindMapExpanded(false);
    };


    // 统一取一个稳定的 video 源，兼容只从 data 里传入 video 的场景
    const currentVideo = video || data?.video;

    // 如果正在加载，显示加载状态
    if (!currentVideo || currentVideo.loading) {
        return (
            <div className={s['detail-container']}>
                <div className={s['empty-state']}>
                    <div className={s['loading-spinner']}></div>
                    <p>正在加载详情...</p>
                </div>
            </div>
        );
    }

    // 如果没有详情数据，显示空状态
    if (!currentVideo.detailInfo) {
        return (
            <div className={s['detail-container']}>
                <div className={s['empty-state']}>
                    <p>暂无详情数据</p>
                </div>
            </div>
        );
    }

    const { detailInfo } = currentVideo;
    // 优先使用 API 返回的 resource_name，否则使用 video.title
    const displayTitle = detailInfo.resource_name || currentVideo.title;

    const handleViewVideo = () => {
        const targetVideo = currentVideo || data?.video;
        const targetIndex = typeof videoIndex === 'number' ? videoIndex : (data?.videoIndex ?? 0);
        const list = Array.isArray(videoList) && videoList.length > 0 ? videoList : (targetVideo ? [targetVideo] : []);
        onVideoClick?.(targetVideo, targetIndex, list);
    };

    return (
        <div className={s['detail-container']}>
            {/* 视频标题 */}
            <div className={s['video-title-section']}>
                <h2 className={s['video-title']}>{displayTitle}</h2>
                {/* 显示课程类型（如果有） */}
                {detailInfo.template_names && detailInfo.template_names.length > 0 && (
                    <div className={s['template-tags']}>
                        {detailInfo.template_names.map((name, idx) => (
                            <span key={idx} className={s['template-tag']}>
                                {name}
                            </span>
                        ))}
                    </div>
                )}
            </div>

            {/* 关键词部分 */}
            <div className={s['section']}>
                <div className={s['section-header']}>
                    <div className={s['section-indicator']}></div>
                    <h3 className={s['section-title']}>关键词</h3>
                </div>
                <div className={s['keywords-container']}>
                    {detailInfo.keywords && detailInfo.keywords.length > 0 ? (
                        detailInfo.keywords.map((keyword, index) => (
                            <span key={index} className={s['keyword-tag']}>
                                {keyword}
                            </span>
                        ))
                    ) : (
                        <p className={s['empty-text']}>暂无关键词</p>
                    )}
                </div>
            </div>

            {/* 视频摘要部分 */}
            <div className={s['section']}>
                <div className={s['section-header']}>
                    <div className={s['section-indicator']}></div>
                    <h3 className={s['section-title']}>视频摘要</h3>
                </div>
                <div className={s['summary-content']}>
                    {detailInfo.summary ? (
                        <p className={s['summary-text']}>{detailInfo.summary}</p>
                    ) : (
                        <p className={s['empty-text']}>暂无摘要</p>
                    )}
                </div>
            </div>

            {/* 思维导图部分 */}
            <div className={s['section']}>
                <div className={s['section-header']}>
                    <div className={s['section-indicator']}></div>
                    <h3 className={s['section-title']}>思维导图</h3>
                </div>
                <div className={s['mindmap-wrapper']}>
                    {detailInfo.mindMap ? (
                        // 判断数据类型：对象（树形结构）、图片 URL 或 markdown 文本
                        (typeof detailInfo.mindMap === 'object' && detailInfo.mindMap !== null && detailInfo.mindMap.title) ? (
                            // 如果是对象（树形结构），直接使用 MindMap 组件渲染
                            <div 
                                className={`${s['mindmap-container']} ${s['clickable']}`}
                                onClick={handleMindMapClick}
                            >
                                <MindMap
                                    mindMapSummaryListData={detailInfo.mindMap.children || []}
                                    courseName={detailInfo.mindMap.title || displayTitle}
                                    width={600}
                                    height={560}
                                    size="16"
                                    disableInteraction={true}
                                />
                            </div>
                        ) : (typeof detailInfo.mindMap === 'string' && 
                             (detailInfo.mindMap.startsWith('http://') || 
                              detailInfo.mindMap.startsWith('https://') || 
                              detailInfo.mindMap.startsWith('/'))) ? (
                            // 如果是 URL，显示图片
                            <div 
                                className={`${s['mindmap-container']} ${s['clickable']}`}
                                onClick={handleMindMapClick}
                            >
                                <img 
                                    src={detailInfo.mindMap} 
                                    alt="思维导图"
                                    className={s['mindmap-image']}
                                    onError={(e) => {
                                        e.target.style.display = 'none';
                                        e.target.nextSibling.style.display = 'flex';
                                    }}
                                />
                                <div className={s['mindmap-placeholder']}>
                                    <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                        <circle cx="12" cy="12" r="10"></circle>
                                        <line x1="12" y1="8" x2="12" y2="12"></line>
                                        <line x1="12" y1="16" x2="12.01" y2="16"></line>
                                    </svg>
                                    <p>思维导图加载失败</p>
                                </div>
                                
                                {/* AI生成水印 */}
                                <div className={s['ai-watermark']}>
                                    内容由AI生成
                                </div>
                            </div>
                        ) : (
                            // 如果是 markdown 文本，使用 MindMapContent 组件渲染
                            <div 
                                className={`${s['mindmap-container']} ${s['clickable']}`}
                                onClick={handleMindMapClick}
                            >
                                <MindMapContent 
                                    data={{
                                        title: displayTitle,
                                        description: detailInfo.mindMap
                                    }}
                                    disableInteraction={true}
                                />
                            </div>
                        )
                    ) : (
                        <div className={s['empty-mindmap']}>
                            <p className={s['empty-text']}>暂无思维导图</p>
                        </div>
                    )}
                </div>
            </div>

            {/* 思维导图放大模态框 */}
            {detailInfo.mindMap && (
                <Modal 
                    centered 
                    width={1200} 
                    title={`${displayTitle} - 思维导图`}
                    maskClosable={false} 
                    footer={null} 
                    open={isMindMapExpanded} 
                    onCancel={handleCloseExpanded}
                    zIndex={1002}
                    styles={{
                        mask: {
                            zIndex: 1002
                        },
                        content: {
                            zIndex: 1002
                        },
                        body: {
                            height: '80vh',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            padding: '24px'
                        }
                    }}
                >
                    {(typeof detailInfo.mindMap === 'object' && detailInfo.mindMap !== null && detailInfo.mindMap.title) ? (
                        // 对象（树形结构）
                        <div style={{ position: 'relative', width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            <MindMap
                                mindMapSummaryListData={detailInfo.mindMap.children || []}
                                courseName={detailInfo.mindMap.title || displayTitle}
                                width={1150}
                                height={730}
                                size="18"
                                markStyle={{ position: 'absolute', right: '16px', bottom: '16px', zIndex: 10 }}
                            />
                        </div>
                    ) : (typeof detailInfo.mindMap === 'string' && 
                         (detailInfo.mindMap.startsWith('http://') || 
                          detailInfo.mindMap.startsWith('https://') || 
                          detailInfo.mindMap.startsWith('/'))) ? (
                        // URL 图片
                        <div style={{ position: 'relative', width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', overflow: 'auto' }}>
                            <img 
                                src={detailInfo.mindMap} 
                                alt="思维导图"
                                style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                            />
                            {/* AI生成水印 */}
                            <div className={s['ai-watermark']}>
                                内容由AI生成
                            </div>
                        </div>
                    ) : (
                        // markdown 文本
                        <div style={{ position: 'relative', width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            <MindMapContent 
                                data={{
                                    title: displayTitle,
                                    description: detailInfo.mindMap
                                }}
                            />
                            {/* AI生成水印 */}
                            <div className={s['ai-watermark']}>
                                内容由AI生成
                            </div>
                        </div>
                    )}
                </Modal>
            )}

            {/* 底部操作区域 */}
            <div className={s['footer-actions']}>
                <button 
                    className={s['view-next-btn']}
                    onClick={handleViewNext}
                >
                    查看下一个
                </button>
                <button 
                    className={s['continue-btn']}
                    onClick={handleViewVideo}
                >
                    查看视频
                </button>
            </div>
        </div>
    );
};

export default VideoDetailContent;

